import { useEffect, useState } from "react";
import "./SetPassword.scss";
import { setPasswordApi } from "../../api/api";

export default function SetPassword() {
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");

    if (!t) {
      setTokenMissing(true);
    } else {
      setToken(t);
    }
  }, []);

  const getStrength = (pw) => {
    let score = 0;

    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;

    return score;
  };

  const strengthScore = getStrength(newPassword);

  const strengthLabel = ["", "Weak", "Weak", "Fair", "Good", "Strong"][
    strengthScore
  ];

  const strengthClass = ["", "weak", "weak", "fair", "good", "strong"][
    strengthScore
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setStatus("error");
      setMessage("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    setStatus(null);
    setMessage("");

    try {
      const { data } = await setPasswordApi({
        token,
        newPassword,
      });

      if (data.success) {
        setStatus("success");
        setMessage(
          data.message || "Password updated successfully. Please log in.",
        );
      } else {
        setStatus("error");
        setMessage(data.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      setStatus("error");
      setMessage(
        error?.response?.data?.message ||
          "Network error. Please check your connection.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (tokenMissing) {
    return (
      <div className="sp-root">
        <div className="sp-card sp-card--error">
          <div className="sp-icon sp-icon--warn">⚠</div>
          <h2>Invalid Link</h2>
          <p>
            This password reset link is missing a token. Please request a new
            one.
          </p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="sp-root">
        <div className="sp-card sp-card--success">
          <div className="sp-icon sp-icon--check">✓</div>
          <h2>Password Set!</h2>
          <p>{message}</p>

          <a className="sp-btn sp-btn--full" href="/login">
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-root">
      <div className="sp-bg">
        <div className="sp-bg__circle sp-bg__circle--1" />
        <div className="sp-bg__circle sp-bg__circle--2" />
        <div className="sp-bg__circle sp-bg__circle--3" />
      </div>

      <div className="sp-card">
        <div className="sp-header">
          <div className="sp-logo">M</div>

          <h1 className="sp-title">Set your password</h1>

          <p className="sp-subtitle">
            Choose a strong password to secure your Murasalone account.
          </p>
        </div>

        <form className="sp-form" onSubmit={handleSubmit} noValidate>
          <div className="sp-field">
            <label className="sp-label" htmlFor="newPassword">
              New Password
            </label>

            <div className="sp-input-wrap">
              <input
                id="newPassword"
                className="sp-input"
                type={showNew ? "text" : "password"}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />

              <button
                type="button"
                className="sp-eye"
                onClick={() => setShowNew((v) => !v)}
                tabIndex={-1}
              >
                {showNew ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            {newPassword.length > 0 && (
              <div className="sp-strength">
                <div className="sp-strength__bars">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`sp-strength__bar ${
                        i <= strengthScore
                          ? `sp-strength__bar--${strengthClass}`
                          : ""
                      }`}
                    />
                  ))}
                </div>

                <span
                  className={`sp-strength__label sp-strength__label--${strengthClass}`}
                >
                  {strengthLabel}
                </span>
              </div>
            )}
          </div>

          <div className="sp-field">
            <label className="sp-label" htmlFor="confirmPassword">
              Confirm Password
            </label>

            <div className="sp-input-wrap">
              <input
                id="confirmPassword"
                className={`sp-input ${
                  confirmPassword.length > 0 && confirmPassword !== newPassword
                    ? "sp-input--error"
                    : confirmPassword.length > 0 &&
                        confirmPassword === newPassword
                      ? "sp-input--ok"
                      : ""
                }`}
                type={showConfirm ? "text" : "password"}
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />

              <button
                type="button"
                className="sp-eye"
                onClick={() => setShowConfirm((v) => !v)}
                tabIndex={-1}
              >
                {showConfirm ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            {confirmPassword.length > 0 && confirmPassword !== newPassword && (
              <span className="sp-hint sp-hint--error">
                Passwords do not match
              </span>
            )}
          </div>

          {status === "error" && (
            <div className="sp-alert sp-alert--error">{message}</div>
          )}

          <button
            type="submit"
            className="sp-btn sp-btn--full"
            disabled={loading}
          >
            {loading ? <span className="sp-spinner" /> : "Set Password"}
          </button>
        </form>

        <p className="sp-footer">
          Murasalone &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
